import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Form, { Validations } from '../../../../../components/Form';
import useRequest from '../../../../../hooks/useRequest';
import { ControlItem } from '../../../types';
import {
  formatObjectFields,
  formatParam,
  getFieldsFromObject,
} from '../../helpers';

import ServicePaths from '../../../../../bridge/utils/ServicePaths';

const EditUserForm = () => {
  const [userId, setUserId] = useState<string | undefined>();
  const [errors, setErrors] = useState<Record<string, Validations>>({});
  const [canFetchUser, setCanFetchUser] = useState(false);
  const [user, setUser] = useState<Record<string, any> | undefined>();
  const [formMessage, setFormMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const params = useMemo(() => ({ userId }), [userId]);
  const options = useMemo(
    () => ({
      shouldFetch: canFetchUser,
    }),
    [canFetchUser],
  );

  const { data: userData } = useRequest(
    ServicePaths.CONTROL.GetUser,
    params,
    options,
  );

  useEffect(() => {
    setUser(userData);
  }, [userData]);

  const checkUserIdValidation = useCallback(() => {
    /** userId can not be empty */

    setErrors({});

    if (typeof userId !== 'undefined' && userId?.length === 0) {
      setErrors((prev) => ({
        ...prev,
        userId: Validations.Required,
      }));
    }

    return true;
  }, [userId]);

  const handleGetUserSubmit = useCallback(() => {
    const canContinue = checkUserIdValidation();

    if (!canContinue) {
      setIsLoading(false);
      return;
    }

    setCanFetchUser(true);
  }, [checkUserIdValidation]);

  const handleSubmit = useCallback(async () => {
    if (!user) {
      return;
    }

    setIsLoading(true);

    /** Edit don't have validation @TODO */

    const { data } = await axios({
      method: 'POST',
      url: ServicePaths.CONTROL.EditUser,
      params: {
        userId: formatParam(userId),
        user: formatObjectFields(user),
      },
    });

    setIsLoading(false);
    setFormMessage(data);
  }, [user, userId]);

  const getUserDataConfig = useMemo(() => {
    return {
      title: ControlItem.EditUser,
      fields: [
        {
          label: 'User Id',
          value: userId,
          handleValueChange: setUserId,
          error: errors.userId,
        },
      ],
      handleSubmit: handleGetUserSubmit,
    };
  }, [errors.userId, handleGetUserSubmit, userId]);

  const config = useMemo(() => {
    if (!user) {
      return null;
    }

    /** Loop over values */
    const fields = getFieldsFromObject(user, errors, setUser);

    return {
      title: ControlItem.EditUser,
      formMessage,
      fields: [
        {
          label: 'User Id',
          value: userId,
          handleValueChange: setUserId,
          error: errors.userId,
          isLocked: true,
        },
        ...fields,
      ],
      handleSubmit,
      isLoading,
    };
  }, [errors, formMessage, handleSubmit, isLoading, user, userId]);

  if (!userData) {
    return <Form config={getUserDataConfig} />;
  }

  if (!config) {
    return null;
  }

  return <Form config={config} />;
};

export default EditUserForm;
